import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import t from 'react-translate';
import moment from 'moment';
import { useAppDispatch } from 'redux/store';
import { isEmpty, pick, reduce, values } from 'underscore';
import { LiveEventSession, RegisteredSessionParams } from 'redux/schemas/models/live-event';
import { getRegisteredSessionDetails } from 'redux/actions/live-events';
import { BasicTableColumn, ColumnType, NvBasicTable } from 'shared/components/nv-basic-table';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import LoadingWrapper, { LoaderType } from 'shared/components/loading-wrapper';
import { downloadCSV } from 'shared/csv-utils';
import ClickableContainer from 'components/clickable-container';
import useInfiniteScroll from 'shared/hooks/use-infinite-scroll';
import LoadingPlaceholder from 'shared/components/loading-placeholder';
import usePrevious from 'shared/hooks/use-previous';

const NameRow = (props: { fullName: string, email: string }) => (
  <React.Fragment>
    <div className='text-regular'>{props.fullName}</div>
    <div className='text-regular'>{props.email}</div>
  </React.Fragment>
);
type RegisteredDetailsModalProps = {
  catalogId: string
  registeredSession: LiveEventSession
  show: boolean
  onClose: () => void
};

const RegisteredDetailsModal = ({
  catalogId,
  registeredSession,
  show,
  onClose,
}: RegisteredDetailsModalProps) => {
  const [registeredUsers, setRegisteredUsers] = useState({});
  const [isPageFetching, setIsPageFetching] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);
  const [onceLoaded, setOnceLoaded] = useState(false);

  const dispatch = useAppDispatch();
  const prevPageNo = usePrevious(pageNo);

  const hasMore = registeredSession?.totalRegisteredUsersCount > values(registeredUsers)?.length;

  const userTable = useRef<HTMLDivElement>(null);
  const isScrolledToBottom = useInfiniteScroll(userTable.current, 0);

  const fetchRegisteredUSers = useCallback(async (fetchAll?: boolean) => {
    const params: RegisteredSessionParams = {
      catalogId,
      liveSessionId: registeredSession.id,
    };
    if (!fetchAll) {
      params.page = pageNo;
    }
    const data = await dispatch(getRegisteredSessionDetails(params))
      .then(result => {
        const userData = reduce(
          result?.payload?.registeredUsers,
          (users, user) => {
            users[user.id] = pick(user, ['id', 'fullName', 'email', 'firstName', 'lastName']);
            return users;
          },
          {},
        );
        if (!fetchAll) {
          setOnceLoaded(true);
          setRegisteredUsers({ ...registeredUsers, ...userData });
          setIsPageFetching(false);
          return null;
        }
        return userData;
      });
    return data;
  }, [catalogId, dispatch, pageNo, registeredSession.id, registeredUsers]);

  useEffect(() => {
    if (show
      && isEmpty(registeredUsers)
      && !onceLoaded
      && !isPageFetching
    ) {
      setIsPageFetching(true);
      fetchRegisteredUSers();
    }
  }, [fetchRegisteredUSers, registeredUsers, onceLoaded, show, isPageFetching]);

  useEffect(() => {
    if (userTable && isScrolledToBottom && !isPageFetching && hasMore) {
      setPageNo(prev => prev + 1);
    }
  }, [hasMore, isPageFetching, isScrolledToBottom]);

  useEffect(() => {
    if (show
      && pageNo > 1
      && pageNo !== prevPageNo
      && hasMore
      && !isPageFetching
    ) {
      setIsPageFetching(true);
      fetchRegisteredUSers();
    }
  }, [fetchRegisteredUSers, hasMore, pageNo, prevPageNo, show, isPageFetching]);

  const downloadAttendeesCSV = useCallback(async () => {
    setIsDownloading(true);
    const registeredUsersData = values(registeredUsers)?.length === registeredSession?.totalRegisteredUsersCount
      ? registeredUsers : await fetchRegisteredUSers(true);
    if (registeredUsersData) {
      const headers = ['First Name', 'Last Name', 'Email'];
      const data = [headers];
      const usersData = values(registeredUsersData).map(({ firstName, lastName, email }) => [firstName, lastName, email]);
      data.push(...usersData);

      const timestamp = (new Date()).toISOString().replace(/[^A-Z0-9]/g, '');
      const csvName = `${registeredSession.title}_${catalogId}_${timestamp}.csv`;

      downloadCSV(data, csvName);
      setTimeout(() => {
        setIsDownloading(false);
      }, 300);
    }
  }, [
    catalogId, fetchRegisteredUSers, registeredSession?.title,
    registeredSession?.totalRegisteredUsersCount, registeredUsers,
  ]);

  const cols: BasicTableColumn[] = [
    {
      key: 'fullName',
      name: '',
      className: 'column',
      sortable: false,
      gridWidth: '60%',
      type: ColumnType.STRING,
    },
    {
      key: 'email',
      name: '',
      className: 'column',
      sortable: false,
      gridWidth: '40%',
      type: ColumnType.STRING,
    },
  ];

  const body = () => (
    <div
      className='d-flex flex-column px-7 mt-4 mb-6'
      css={css`
        .disabled {
          opacity: 0.4;
        }
        .user-table {
          height: 520px;
          overflow-y: auto;
        }
        .basic-table {
          margin-top: 0px !important;
        }
      `}
    >
      <div className='text-large-regular font-weight-bolder'>
        {registeredSession?.title}
      </div>
      <div className='text-date'>
        {moment(registeredSession?.startTime).format('LLL')}
      </div>
      <div className='d-flex font-weight-bolder mb-4'>
        <div className='text-regular'>
          {t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.REGISTRATION.COUNT(registeredSession?.totalRegisteredUsersCount)}
        </div>
        {!isEmpty(registeredUsers) && (
          <ClickableContainer
            className={`ml-1 text-primary ${isDownloading ? 'disabled' : ''}`}
            onClick={downloadAttendeesCSV}
            disabled={isDownloading}
          >
            ({isDownloading ? t.FILE_UPLOAD.DOWNLOADING() : t.FILE_UPLOAD.DOWNLOAD()})
          </ClickableContainer>
        )}
      </div>
      <div ref={userTable} className='user-table pb-2'>
        <LoadingWrapper
          loaderType={LoaderType.PLACEHOLDER}
          isLoaded={onceLoaded}
          className='w-100 mt-5'
        >
          <NvBasicTable<JSX.Element, { id: number, fullName: string, email: string }>
            columns={cols}
            tabledata={registeredUsers as any}
            rowComponent={NameRow as FunctionComponent}
            rowClass='p-3'
            showHeader={false}
            highlightClass=''
          />
          {isPageFetching && <LoadingPlaceholder />}
        </LoadingWrapper>
      </div>
    </div>
  );

  return (
    <NvModal
      show={show}
      type={ModalType.DYNAMIC}
      header={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.REGISTRATION.TITLE()}
      body={body()}
      onClose={onClose}
      fullHeight={false}
    />
  );
};

export default RegisteredDetailsModal;
